import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  TopStrip, Nav, FooterFull, CtaBand,
  ProposalModal,
  PACKAGES, COMPARISON,
  useOpenModalFromQuery,
  type PackageName, type ModalKind,
} from './landing-shared';
import './landing.css';

type Mark = 'yes' | 'no' | string;

function MarkCell({ value }: { value: Mark }) {
  if (value === 'yes') return <span className="icon-yes">✓</span>;
  if (value === 'no') return <span className="icon-no">·</span>;
  return <span className="icon-partial">{value}</span>;
}

export function PackagesPage() {
  const [annual, setAnnual] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);
  const [selectedPackage, setSelectedPackage] = useState<PackageName | ''>('');
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Pre-select package from ?selected=Advanced (linked from diagnostic).
  useEffect(() => {
    const sel = searchParams.get('selected');
    if (sel === 'Basic' || sel === 'Advanced' || sel === 'Custom') {
      const el = document.getElementById(`pkg-${sel.toLowerCase()}`);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [searchParams]);

  // Smooth scroll to hash (e.g. /packages#choose-your-package or /packages#compare)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [location.hash]);

  // Open proposal modal from ?open=proposal&pkg=…
  useOpenModalFromQuery(setModal, setSelectedPackage);

  // Reveal-on-scroll
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      }),
      { threshold: 0.1 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const openProposal = (pkg?: PackageName) => {
    setSelectedPackage(pkg ?? '');
    setModal('proposal');
  };

  const fmt = (n: number) => '€' + n.toLocaleString('en-IE');

  const priced = useMemo(() => {
    return PACKAGES.map((p) => {
      if (p.priceMonthly == null) return { ...p, displayPrice: null, originalPrice: null };
      const discounted = annual ? Math.round(p.priceMonthly * 0.9) : p.priceMonthly;
      return {
        ...p,
        displayPrice: fmt(discounted),
        originalPrice: annual ? fmt(p.priceMonthly) : null,
      };
    });
  }, [annual]);

  return (
    <div className="wedpo-landing">
      <TopStrip />
      <Nav active="packages" onProposal={() => openProposal()} />

      <section className="page-hero">
        <div className="page-eyebrow">Pricing</div>
        <h1 className="page-title" id="choose-your-package">Choose your <em>package</em></h1>
        <p className="page-sub">
          Three clearly defined packages. All include a named DPO and platform access from day one.
        </p>
      </section>

      <section className="section section--packages">
        <div className="section-inner">
          <div className="billing-toggle">
            <span className={`toggle-label${!annual ? ' active' : ''}`}>Monthly</span>
            <button
              type="button"
              className={`toggle-track${annual ? ' annual' : ''}`}
              onClick={() => setAnnual((v) => !v)}
              aria-label="Toggle annual billing"
            >
              <div className="toggle-thumb" />
            </button>
            <span className={`toggle-label${annual ? ' active' : ''}`}>
              Annual <span className={`annual-badge${annual ? ' show' : ''}`}>Save 10%</span>
            </span>
          </div>

          <div className="packages-grid">
            {priced.map((p) => (
              <div
                key={p.name}
                id={`pkg-${p.name.toLowerCase()}`}
                className={`pkg-card${p.featured ? ' featured' : ''}`}
              >
                {p.featured && <div className="pkg-badge">Most Popular</div>}
                <div className="pkg-tier">{p.tier}</div>
                <div className="pkg-name">{p.name}</div>
                <p className="pkg-desc">{p.desc}</p>
                <div className="pkg-price-area">
                  {p.displayPrice == null ? (
                    <>
                      <div className="pkg-price pkg-price--proposal">Proposal only</div>
                      <div className="pkg-billing">&nbsp;</div>
                    </>
                  ) : (
                    <>
                      <div className="pkg-price">
                        {p.originalPrice && (
                          <span className="price-original show">{p.originalPrice}</span>
                        )}
                        <span>{p.displayPrice}</span>
                        <span>/ month</span>
                      </div>
                      <div className="pkg-billing">
                        {annual ? 'Billed annually (save 10%)' : 'Billed monthly'}
                      </div>
                    </>
                  )}
                  <div className="pkg-ideal">{p.ideal}</div>
                </div>
                <ul className="pkg-features">
                  {p.features.map((f) => (
                    <li key={f}>
                      <div className="check">✓</div>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => openProposal(p.name)}
                  className={`pkg-cta ${
                    p.ctaStyle === 'primary'
                      ? 'pkg-cta-primary'
                      : p.ctaStyle === 'dark'
                        ? 'pkg-cta-dark'
                        : 'pkg-cta-outline'
                  }`}
                >
                  {p.ctaLabel}
                </button>
              </div>
            ))}
          </div>

          <div className="compare-anchor">
            <a href="#compare">Or see the full feature comparison ↓</a>
          </div>
        </div>
      </section>

      <section className="compare-section" id="compare">
        <div className="section-inner">
          <div className="compare-header">
            <div className="compare-eyebrow">Feature breakdown</div>
            <h2 className="compare-title">What's included in <em>each package</em></h2>
            <p className="compare-sub">
              Side by side, across every dimension of the service. Core DPO work, the compliance programme, advisory, product privacy, and delivery.
            </p>
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-lg)' }}>
            <table className="comparison-table">
              <tbody>
                {COMPARISON.map((grp) => (
                  <ComparisonGroup key={grp.section} group={grp} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <CtaBand onProposal={() => openProposal()} />
      <FooterFull />

      {modal === 'proposal' && (
        <ProposalModal
          onClose={() => setModal(null)}
          initialPackage={selectedPackage}
          onClearPackage={() => setSelectedPackage('')}
        />
      )}
    </div>
  );
}

function ComparisonGroup({ group }: { group: typeof COMPARISON[number] }) {
  return (
    <>
      <tr className="section-row">
        <td colSpan={4}>{group.section}</td>
      </tr>
      {group.rows.map(([label, a, b, c]) => (
        <tr key={label}>
          <td>{label}</td>
          <td><MarkCell value={a} /></td>
          <td><MarkCell value={b} /></td>
          <td><MarkCell value={c} /></td>
        </tr>
      ))}
    </>
  );
}
